/**
 * NotificationsBell.js — In-app inbox bell shown in every layout header.
 *
 * Polls /api/push/inbox every 30s for unread count and renders a popover
 * with the recent notifications. Click a row → marks as read + navigates
 * to its URL.
 */
import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Bell, BellRing, CheckCheck } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsBell = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/push/inbox?limit=20`, { headers: getAuthHeaders() });
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch { /* silent — bell stays empty */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    timerRef.current = setInterval(load, 30000); // 30s poll
    return () => clearInterval(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleClick = async (n) => {
    if (!n.read) {
      try {
        await axios.post(`${API}/push/inbox/${n.id}/read`, {}, { headers: getAuthHeaders() });
      } catch { /* swallow */ }
    }
    setOpen(false);
    if (n.url && n.url !== '/') navigate(n.url);
    load();
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/push/inbox/read-all`, {}, { headers: getAuthHeaders() });
      load();
    } catch { /* swallow */ }
  };

  const Icon = unread > 0 ? BellRing : Bell;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell-btn">
          <Icon className={`w-5 h-5 ${unread > 0 ? 'text-amber-500' : 'text-slate-500'}`} />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-rose-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notif-bell-popover">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-slate-50">
          <p className="text-sm font-semibold text-slate-700">Notificaciones</p>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-[11px] text-blue-600 hover:underline flex items-center gap-1" data-testid="notif-mark-all-read">
              <CheckCheck className="w-3 h-3" /> Marcar todas leídas
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-8">Sin notificaciones todavía.</p>
          ) : items.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={`block w-full text-left px-3 py-2.5 border-b last:border-0 hover:bg-slate-50 ${!n.read ? 'bg-blue-50/40' : ''}`}
              data-testid={`notif-item-${n.id.slice(0, 8)}`}
            >
              <div className="flex items-start gap-2">
                {!n.read && <span className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-1.5 shrink-0" />}
                <div className={`flex-1 ${n.read ? 'opacity-70' : ''}`}>
                  <p className="text-sm font-medium text-slate-900">{n.title}</p>
                  <p className="text-xs text-slate-600 line-clamp-2">{n.body}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsBell;
